import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getProspect,
  getProspectCount,
  getSequenceList,
  getStepList,
  getStepDetailsList,
  addContactsToStep,
  removeProspect,
  unsubscribeProspect,
  getSingleProspectDetails,
  getMinimalSingleProspectSequencesDetails,
  singleProspectSequencesMarkAsReplied,
  singleProspectSequencesMarkAsFinished,
  singleProspectRemoveFromSequences,
  saveContactAttributes,
  saveContactDealValue,
  addProspectManually,
  verifyProspects,
  getTags,
  addTagsToProspects,
  removeTagsFromProspects,
  verifyBulkProspects,
  deleteBulkProspects,
  unsubscribeBulkProspects,
  addBulkProspectsToStep,
  addTagsToBulkProspects,
  removeTagsFromBulkProspects,
  deleteSecondaryEmail,
  deleteSecondaryPhone,
  changePrimaryEmailInProspect,
  getProspectList,
  getProspectListCount,
  getProspectListByIds,
  getFilterViewList,
  pipUnpinSavedFilter,
  updateSavedFilter,
  deleteSavedView,
  getProspectFiltersFieldsList,
  getProspectFiltersFieldValues,
  getProspectFiltersAttributeFieldValues,
  getProspectFiltersSequencesList,
  getProspectFiltersClientsList,
  saveProspectFiltersView,
  updateProspectFiltersView,
  deleteProspectFiltersView,
  getContactListByViewId,
  getImportedFilesList,
  updateProspectFieldById,
  getLongTextByProspectAndColumnId,
  getAiColumnTemplates,
  getAiModels,
  getTemplateById,
  createAiColumnTemplate,
  updateAiColumnTemplate,
  deleteAiColumnTemplate,
  saveAndDontRunRequest,
  getAiColumnPreview,
  getTemplateConfig,
  saveRegeneratedTemplate,
  aiColPlayPauseEnrichment,
  aiColActiveFieldsStatus,
  updatePrimaryNumber,
  getEligibilityCheck,
  getKanbanViewList,
  updateKanbanView,
  createCustomField,
  updateCustomField,
  getProspectNotes,
  createProspectNotesList,
  deleteProspectNote,
  prospectNotePin,
  updateProspectNote,
  updateProspectNoteVisibility,
  getSinglePropsectDetailPinnedFields,
  updateProspectPinStatus,
  getMinimalEmailAccountList,
  getProspectActivityCount,
} from './helpers/prospect.api';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';
import { getCompanyListsAPI } from '../v2-lead/helpers/leads.api';
import { LeadFinderListTab } from '../v2-lead/types';
import {
  AddProspectManuallyPayload,
  AddToStepParams,
  markAsRepliedParam,
  ProspectCountParams,
  ProspectParams,
  removeFromSequenceParam,
  SaveContactAttributesPayload,
  saveContactDealValuePayload,
  AddTagsToProspectsPayload,
  RemoveTagsFromProspectsPayload,
  BulkActionPayload,
  changePrimaryEmailInProspectPayload,
  deleteSecondaryEmailParams,
  deleteSecondaryPhoneParams,
  ProspectListPayload,
  ProspectListByIdsPayload,
  PinUnpinActionType,
  ProspectFiltersValuesPayload,
  ProspectFiltersSequencesClientsPayload,
  ProspectFiltersSaveViewPayload,
  ProspectFiltersUpdateViewPayload,
  TagsGenerationType,
  ProspectFieldPayload,
  getTemplateByIdParams,
  createAiColumnTemplatePayload,
  // deleteAiColumnTemplatePayload,
  saveAndDontRunPayload,
  getAiColumnPreviewProps,
  saveRegeneratedTemplateProps,
  aiColPlayPauseEnrichmentProps,
  aiColActiveFieldsStatusProps,
  getEligibilityCheckProps,
  UpdateKanbanViewPayload,
  UpdateCustomFieldRequestPayload,
  CreateProspectNoteProp,
  ProspectNotePin,
  UpdateProspectNoteProps,
  UpdateProspectNoteVisibilityProps,
  getProspectNotesParams,
  UpdateProspectPinStatusProps,
  getMinimalEmailAccountListProps,
} from './types';
import { getFieldsGeneric } from '../settings/helpers/field.api';
import { GetFieldsQueryParams } from '../settings/types/request-payload';
import {
  getMembersList,
  getTeamsAndMembersList,
} from '../settings/helpers/users-and-teams.api';
import { getAllOutcomes } from '../settings/helpers/outcome.api';
import { UserStatusKeys } from '../settings/enums/users-and-teams';
import { getMinimalSubsequencesListForFilter } from '../unified-inbox/helpers/unified-inbox.api';
import { EnrichmentTemplateCategory } from './enums/prospect';

export const getProspectListRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectList', async (args, thunkAPI) => {
  try {
    return await getProspectList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectListCountRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectListCount', async (args, thunkAPI) => {
  try {
    return await getProspectListCount(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectListByIdsRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectListByIdsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectListByIds', async (args, thunkAPI) => {
  try {
    return await getProspectListByIds(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectParams,
  { rejectValue: ResponseErrorWithHandled }
>('contact/getProspectRequest', async (args: ProspectParams, thunkAPI) => {
  try {
    return await getProspect(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectCountRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectCountParams,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contact/getProspectCountRequest',
  async (args: ProspectCountParams, thunkAPI) => {
    try {
      return await getProspectCount(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getSequenceListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('sequence/getSequenceList', async (args, thunkAPI) => {
  try {
    return await getSequenceList();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getStepListRequest = createAsyncThunk<
  ResponseSuccess,
  { sequenceId: string },
  { rejectValue: ResponseErrorWithHandled }
>('sequence/getSequenceStepList', async ({ sequenceId }, thunkAPI) => {
  try {
    return await getStepList(sequenceId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getStepDetailsListRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('sequence/getStepDetailsList', async (args, thunkAPI) => {
  try {
    return await getStepDetailsList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const addToStepRequest = createAsyncThunk<
  ResponseSuccess,
  AddToStepParams,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/addToStep', async (payload, thunkAPI) => {
  try {
    return await addContactsToStep(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const removeProspectRequest = createAsyncThunk<
  ResponseSuccess,
  number[],
  { rejectValue: ResponseErrorWithHandled }
>('contacts/removeProspectRequest', async (args, thunkAPI) => {
  try {
    return await removeProspect(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSingleProspectDetailsRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getSingleProspectDetailsRequest', async (args, thunkAPI) => {
  try {
    return await getSingleProspectDetails(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getSingleProspectSequencesDetailsRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/getSingleProspectSequencesDetailsRequest',
  async (args, thunkAPI) => {
    try {
      return await getMinimalSingleProspectSequencesDetails(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const singleProspectSequencesMarkAsRepliedRequest = createAsyncThunk<
  ResponseSuccess,
  markAsRepliedParam[],
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/singleProspectSequencesMarkAsRepliedRequest',
  async (args, thunkAPI) => {
    try {
      return await singleProspectSequencesMarkAsReplied(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const singleProspectSequencesMarkAsFinishedRequest = createAsyncThunk<
  ResponseSuccess,
  markAsRepliedParam[],
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/singleProspectSequencesMarkAsFinishedRequest',
  async (args, thunkAPI) => {
    try {
      return await singleProspectSequencesMarkAsFinished(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const singleProspectRemoveFromSequencesRequest = createAsyncThunk<
  ResponseSuccess,
  removeFromSequenceParam,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/singleProspectRemoveFromSequencesRequest',
  async (args, thunkAPI) => {
    try {
      return await singleProspectRemoveFromSequences(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const unsubscribeProspectRequest = createAsyncThunk<
  ResponseSuccess,
  number[],
  { rejectValue: ResponseErrorWithHandled }
>('contacts/unsubscribeProspectRequest', async (args, thunkAPI) => {
  try {
    return await unsubscribeProspect(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const saveContactAttributesRequest = createAsyncThunk<
  ResponseSuccess,
  SaveContactAttributesPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/saveContactAttributesRequest',
  async ({ contactId, attributes }, thunkAPI) => {
    try {
      return await saveContactAttributes(contactId, attributes);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const saveContactDealValueRequest = createAsyncThunk<
  ResponseSuccess,
  saveContactDealValuePayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/saveContactDealValueRequest',
  async ({ sequenceId, attributes }, thunkAPI) => {
    try {
      return await saveContactDealValue(sequenceId, attributes);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getFieldsRequest = createAsyncThunk<
  ResponseSuccess,
  GetFieldsQueryParams | null,
  { rejectValue: ResponseErrorWithHandled }
>('prospect/getFieldsRequest', async (args = {}, thunkAPI) => {
  try {
    return await getFieldsGeneric({ includeDefaults: true, ...args });
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const addProspectManuallyRequest = createAsyncThunk<
  ResponseSuccess,
  AddProspectManuallyPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/addProspectManuallyRequest', async ({ attributes }, thunkAPI) => {
  try {
    return await addProspectManually(attributes);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const verifyProspectsManuallyRequest = createAsyncThunk<
  ResponseSuccess,
  { contactIds: number[] },
  { rejectValue: ResponseErrorWithHandled }
>('contacts/verifyProspectManuallyRequest', async (args, thunkAPI) => {
  try {
    return await verifyProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getTagsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contact/getTagsRequest', async (args, thunkAPI) => {
  try {
    return await getTags(args);
  } catch (err) {
    return thunkAPI.rejectWithValue(err);
  }
});

export const addTagsToProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  AddTagsToProspectsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/addTagsToProspectsRequest', async (args, thunkAPI) => {
  try {
    return await addTagsToProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const removeTagsFromProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  RemoveTagsFromProspectsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/removeTagsFromProspectsRequest', async (args, thunkAPI) => {
  try {
    return await removeTagsFromProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const verifyBulkProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/verifyBulkProspectsRequest', async (args, thunkAPI) => {
  try {
    return await verifyBulkProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const deleteBulkProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteBulkProspectsRequest', async (args, thunkAPI) => {
  try {
    return await deleteBulkProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const unsubscribeBulkProspectRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/unsubscribeBulkProspectRequest', async (args, thunkAPI) => {
  try {
    return await unsubscribeBulkProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const addBulkProspectsToStepRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/addBulkProspectsToStepRequest', async (args, thunkAPI) => {
  try {
    return await addBulkProspectsToStep(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const addTagsToBulkProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/addTagsToBulkProspectsRequest', async (args, thunkAPI) => {
  try {
    return await addTagsToBulkProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const removeTagsFromBulkProspectsRequest = createAsyncThunk<
  ResponseSuccess,
  BulkActionPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/removeTagsFromBulkProspectsRequest', async (args, thunkAPI) => {
  try {
    return await removeTagsFromBulkProspects(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const changePrimaryEmailInProspectRequest = createAsyncThunk<
  ResponseSuccess,
  changePrimaryEmailInProspectPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/changePrimaryEmailInProspectRequest', async (args, thunkAPI) => {
  try {
    return await changePrimaryEmailInProspect(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const deleteSecondaryEmailRequest = createAsyncThunk<
  ResponseSuccess,
  deleteSecondaryEmailParams,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteSecondaryEmail', async (args, thunkAPI) => {
  try {
    return await deleteSecondaryEmail(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const deleteSecondaryPhoneRequest = createAsyncThunk<
  ResponseSuccess,
  deleteSecondaryPhoneParams,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteSecondaryPhone', async (args, thunkAPI) => {
  try {
    return await deleteSecondaryPhone(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getCustomFieldRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getFieldsGeneric', async (args, thunkAPI) => {
  try {
    return await getFieldsGeneric({ includeDefaults: false });
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getFilterViewListRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getFilterViewList', async (args, thunkAPI) => {
  try {
    return await getFilterViewList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersFieldsListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersFieldsList', async (_args, thunkAPI) => {
  try {
    return await getProspectFiltersFieldsList();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const pinUnpinSavedFilterRequest = createAsyncThunk<
  ResponseSuccess,
  { viewId: number; action: PinUnpinActionType },
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/pinUnpinSavedFilterRequest',
  async ({ viewId, action }, thunkAPI) => {
    try {
      return await pipUnpinSavedFilter(viewId, action);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const updateSavedFilterRequest = createAsyncThunk<
  ResponseSuccess,
  {
    viewId: number;
    payload: { filterName: string; isShared: boolean; teamIds: number[] };
  },
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/updateSavedFilterRequest',
  async ({ viewId, payload }, thunkAPI) => {
    try {
      return await updateSavedFilter(viewId, payload);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const deleteSavedViewRequest = createAsyncThunk<
  ResponseSuccess,
  { viewId: number },
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteSavedViewRequest', async ({ viewId }, thunkAPI) => {
  try {
    return await deleteSavedView(viewId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersFieldValuesRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersValuesPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersFieldValues', async (args, thunkAPI) => {
  try {
    return await getProspectFiltersFieldValues(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectLeadFinderListsRequest = createAsyncThunk<
  ResponseSuccess,
  { key: string },
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectLeadFinderListsRequest', async (_args, thunkAPI) => {
  try {
    return await getCompanyListsAPI(LeadFinderListTab.People);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as any);
  }
});

export const getProspectFiltersAttributeFieldValuesRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersValuesPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersAttributeFieldValues', async (args, thunkAPI) => {
  try {
    return await getProspectFiltersAttributeFieldValues(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersSequencesListRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersSequencesClientsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersSequencesList', async (args, thunkAPI) => {
  try {
    return await getProspectFiltersSequencesList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersClientsListRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersSequencesClientsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersClientsList', async (args, thunkAPI) => {
  try {
    return await getProspectFiltersClientsList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersTagsListRequest = createAsyncThunk<
  ResponseSuccess,
  TagsGenerationType,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersTagsList', async (args, thunkAPI) => {
  try {
    return await getTags(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersOwnersListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersOwnersList', async (_args, thunkAPI) => {
  try {
    return await getTeamsAndMembersList();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersOwnersListForAgencyRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/getProspectFiltersOwnersListForAgencyRequest',
  async (_args, thunkAPI) => {
    try {
      return await getMembersList([UserStatusKeys.Active]);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getProspectFiltersOutcomeListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersOutcomeList', async (_args, thunkAPI) => {
  try {
    return await getAllOutcomes();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectFiltersSubsequenceListRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersSequencesClientsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectFiltersSubsequenceList', async (args, thunkAPI) => {
  try {
    return await getMinimalSubsequencesListForFilter({
      page: args.page,
      pageSize: args.pageSize,
      stepsRequired: false,
    });
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const saveProspectFiltersViewRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersSaveViewPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/saveProspectFiltersView', async (args, thunkAPI) => {
  try {
    return await saveProspectFiltersView(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateProspectFiltersViewRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFiltersUpdateViewPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateProspectFiltersView', async (args, thunkAPI) => {
  try {
    return await updateProspectFiltersView(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const deleteProspectFiltersViewRequest = createAsyncThunk<
  ResponseSuccess,
  string,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteProspectFiltersView', async (args, thunkAPI) => {
  try {
    return await deleteProspectFiltersView(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getContactListByViewIdRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getContactListByViewIdRequest', async (args, thunkAPI) => {
  try {
    return await getContactListByViewId(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getImportedFilesListRequest = createAsyncThunk<
  ResponseSuccess,
  string | undefined,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getImportedFilesListRequest', async (ids, thunkAPI) => {
  try {
    return await getImportedFilesList(ids);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});
export const updateProspectFieldByIdRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFieldPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateProspectFieldByIdRequest', async (args, thunkAPI) => {
  try {
    return await updateProspectFieldById(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getLongTextByProspectAndColumnIdRequest = createAsyncThunk<
  ResponseSuccess,
  ProspectFieldPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/getLongTextByProspectAndColumnIdRequest',
  async (args, thunkAPI) => {
    try {
      return await getLongTextByProspectAndColumnId(args);
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const getTemplateOptionList = createAsyncThunk<
  ResponseSuccess,
  {
    page?: number;
    perPage?: number;
    append?: boolean;
    search?: string;
    category?: EnrichmentTemplateCategory;
  },
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/mockOptionListing', async (args, thunkAPI) => {
  try {
    return await getAiColumnTemplates(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const saveAndDontRun = createAsyncThunk<
  ResponseSuccess,
  saveAndDontRunPayload,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/saveAndDontRun', async (args, thunkAPI) => {
  try {
    return await saveAndDontRunRequest(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getAiModelRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/getSupportedAiModel', async (_, thunkAPI) => {
  try {
    return await getAiModels();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getTemplateByIdRequest = createAsyncThunk<
  ResponseSuccess,
  getTemplateByIdParams,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/getTemplateById', async (args, thunkAPI) => {
  try {
    return await getTemplateById(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const createAiColumnTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  createAiColumnTemplatePayload,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/createAiColumnTemplate', async (args, thunkAPI) => {
  try {
    return await createAiColumnTemplate(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const updateAiColumnTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  { templateId: number; payload: Partial<createAiColumnTemplatePayload> },
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/updateAiColumnTemplate', async (args, thunkAPI) => {
  try {
    return await updateAiColumnTemplate(args.templateId, args.payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const deleteAiColumnTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/deleteAiColumnTemplate', async (templateId, thunkAPI) => {
  try {
    return await deleteAiColumnTemplate(templateId);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getAiColumnPreviewRequest = createAsyncThunk<
  ResponseSuccess,
  getAiColumnPreviewProps,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/templatePreveiw', async (args, thunkAPI) => {
  try {
    return await getAiColumnPreview(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getTemplateConfigResponse = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/templateConfig', async (args, thunkAPI) => {
  try {
    return await getTemplateConfig(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const regenerateAiColumnCellRequest = createAsyncThunk<
  ResponseSuccess,
  saveRegeneratedTemplateProps,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/saveRegeneratedTemplate', async (args, thunkAPI) => {
  try {
    return await saveRegeneratedTemplate(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const aiColPlayPauseEnrichmentRequest = createAsyncThunk<
  ResponseSuccess,
  aiColPlayPauseEnrichmentProps,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/aiColPlayPauseEnrich', async (args, thunkAPI) => {
  try {
    return await aiColPlayPauseEnrichment(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const aiColActiveFieldsEnrichmentStatus = createAsyncThunk<
  ResponseSuccess,
  aiColActiveFieldsStatusProps,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/aiColActiveFieldsStatus', async (args, thunkAPI) => {
  try {
    return await aiColActiveFieldsStatus(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const updatePrimaryNumberRequest = createAsyncThunk<
  ResponseSuccess,
  { contactId: number; primaryPhoneProspectInfoId: number },
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/updatePrimaryNumberRequest',
  async ({ contactId, primaryPhoneProspectInfoId }, thunkAPI) => {
    try {
      return await updatePrimaryNumber({
        contactId,
        primaryPhoneProspectInfoId,
      });
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const getEligibilityCheckRequest = createAsyncThunk<
  ResponseSuccess,
  getEligibilityCheckProps,
  { rejectValue: ResponseErrorWithHandled }
>('ai-column/getEligibilityCheck', async (args, thunkAPI) => {
  try {
    return await getEligibilityCheck(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getKanbanListRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getKanbanList', async (_, thunkAPI) => {
  try {
    return await getKanbanViewList();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateKanbanViewRequest = createAsyncThunk<
  ResponseSuccess,
  { kanbanViewId: number; payload: UpdateKanbanViewPayload },
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateKanbanView', async ({ kanbanViewId, payload }, thunkAPI) => {
  try {
    return await updateKanbanView(kanbanViewId, payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateCustomFieldRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateCustomFieldRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateCustomFieldRequest', async (args, thunkAPI) => {
  try {
    return await updateCustomField(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const createCustomFieldRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateCustomFieldRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/createCustomFieldRequest', async (args, thunkAPI) => {
  try {
    return await createCustomField(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getProspectNotesRequest = createAsyncThunk<
  ResponseSuccess,
  getProspectNotesParams,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/prospectNotes', async (args, thunkAPI) => {
  try {
    return await getProspectNotes(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const createProspectNotesListRequest = createAsyncThunk<
  ResponseSuccess,
  CreateProspectNoteProp,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/createProspectNotes', async (args, thunkAPI) => {
  try {
    return await createProspectNotesList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Delete Prospect Note
export const deleteProspectNoteRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/deleteProspectNotes', async (args, thunkAPI) => {
  try {
    return await deleteProspectNote(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Prospect Note Pin
export const prospectNotePinRequest = createAsyncThunk<
  ResponseSuccess,
  { noteId: number; payload: ProspectNotePin },
  { rejectValue: ResponseErrorWithHandled }
>('contacts/prospectNotesPin', async ({ noteId, payload }, thunkAPI) => {
  try {
    return await prospectNotePin(noteId, payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Update Prospect Note
export const updateProspectNoteRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateProspectNoteProps,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateProspectNotes', async (payload, thunkAPI) => {
  try {
    return await updateProspectNote(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const updateProspectNoteVisibilityRequest = createAsyncThunk<
  ResponseSuccess,
  { noteId: number; payload: UpdateProspectNoteVisibilityProps },
  { rejectValue: ResponseErrorWithHandled }
>(
  'contacts/updateProspectNoteVisibility',
  async ({ noteId, payload }, thunkAPI) => {
    try {
      return await updateProspectNoteVisibility(noteId, payload);
    } catch (e) {
      return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
    }
  },
);

// get Single Propsect Detail Pinned Fields
export const getSinglePropsectDetailPinnedFieldsRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getSingleProspectDetailPinnedFields', async (_, thunkAPI) => {
  try {
    return await getSinglePropsectDetailPinnedFields();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// update Prospect Pin Status
export const updateProspectPinStatusRequest = createAsyncThunk<
  ResponseSuccess,
  UpdateProspectPinStatusProps,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/updateProspectPin', async (args, thunkAPI) => {
  try {
    return await updateProspectPinStatus(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// get Minimal Email Account List
export const getMinimalEmailAccountListRequest = createAsyncThunk<
  ResponseSuccess,
  getMinimalEmailAccountListProps,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/emailAccMinimalList', async (args, thunkAPI) => {
  try {
    return await getMinimalEmailAccountList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// get Prospect Activity Count
export const getProspectActivityCountRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('contacts/getProspectActivityCount', async (args, thunkAPI) => {
  try {
    return await getProspectActivityCount(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});
